/**
 * Thin fetch wrapper for the Flask backend.
 *
 * BASE already includes the /api prefix the Flask app mounts every blueprint
 * under (https://host/api), so paths passed to post() start at /v1/... — adding
 * /api here too would produce /api/api/v1/...
 *
 * The endpoints live in product_server/routes/lead_routes.py on the dev branch.
 * See docs/API-CONTRACT.md.
 */
const BASE = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, '') ?? '';

export class ApiError extends Error {
  constructor(message, { code, field, status, structured = false } = {}) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.field = field;
    this.status = status;
    /**
     * True when the server returned our documented error envelope, i.e. it
     * understood the request and is telling the visitor something they can act
     * on. False for a bare 404/502/HTML error page, which means the endpoint is
     * not there — a different problem, and not one to show the visitor.
     */
    this.structured = structured;
  }
}

export const isApiConfigured = () => Boolean(BASE);

export async function post(path, body, { signal, timeoutMs = 10000 } = {}) {
  if (!BASE) {
    throw new ApiError('The submission endpoint is not configured yet.', {
      code: 'NOT_CONFIGURED',
    });
  }

  // AbortSignal.timeout is not in every browser we support; compose manually.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  signal?.addEventListener('abort', () => controller.abort(), { once: true });

  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err) {
    throw new ApiError(
      err.name === 'AbortError' ? 'That took too long — please try again.' : 'Network error.',
      { code: err.name === 'AbortError' ? 'TIMEOUT' : 'NETWORK' },
    );
  } finally {
    clearTimeout(timer);
  }

  const payload = await res.json().catch(() => null);

  if (!res.ok || payload?.ok === false) {
    const error = payload?.error;
    throw new ApiError(error?.message ?? 'That did not go through.', {
      code: error?.code,
      field: error?.field,
      status: res.status,
      structured: Boolean(error?.message),
    });
  }

  return payload;
}
